import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw, Download, CheckCircle2, XCircle } from 'lucide-react';
import exportSheetToPdf from '../../utils/exportSheetToPdf';

const normalize = (value) => (value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const isCorrectAnswer = (question, userAnswer) => {
  if (userAnswer === undefined || userAnswer === null || userAnswer === '') return false;
  if (question.type === 'fill_blank') return normalize(userAnswer) === normalize(question.correctAnswer);
  return userAnswer === question.correctAnswer;
};

const ScoreRing = ({ percent }) => {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  const color = percent >= 80 ? '#10b981' : percent >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <svg width="112" height="112" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth="8" />
      <circle
        cx="50" cy="50" r={radius} fill="none" stroke={color} strokeWidth="8"
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 50 50)"
        style={{ transition: 'stroke-dashoffset 0.8s ease' }}
      />
      <text x="50" y="50" textAnchor="middle" dominantBaseline="central" fontSize="20" fontWeight="800" fill="hsl(var(--foreground))">
        {percent}%
      </text>
    </svg>
  );
};

const QuestionPrompt = ({ question, index, answer, onAnswer, disabled }) => (
  <div>
    <p className="text-base font-bold text-foreground mb-5 leading-relaxed">{question.question}</p>

    {question.type === 'fill_blank' ? (
      <input
        type="text"
        value={answer || ''}
        onChange={(e) => onAnswer(e.target.value)}
        disabled={disabled}
        placeholder="Type your answer..."
        className="w-full px-4 py-3 rounded-xl bg-muted border-2 border-transparent focus:border-primary focus:outline-none text-sm text-foreground disabled:opacity-60"
        autoFocus
      />
    ) : (
      <div className="flex flex-col gap-2">
        {(question.options || []).map((opt, i) => (
          <button
            key={i}
            type="button"
            disabled={disabled}
            onClick={() => onAnswer(opt)}
            className={`text-left px-4 py-3 rounded-xl border-2 text-sm font-medium transition-colors cursor-pointer disabled:cursor-default ${
              answer === opt
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-muted/40 text-foreground hover:border-primary/40'
            }`}
          >
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-current text-[10px] font-bold mr-2 align-middle flex-shrink-0">
              {String.fromCharCode(65 + i)}
            </span>
            {opt}
          </button>
        ))}
      </div>
    )}
  </div>
);

const QuizPlayer = ({ questions, title, materialTitle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    setCurrentIndex(0);
    setAnswers({});
    setSubmitted(false);
  }, [questions]);

  const total = questions.length;
  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).filter((k) => answers[k] !== '' && answers[k] !== undefined).length;
  const isLast = currentIndex === total - 1;

  const setAnswer = (value) => setAnswers((prev) => ({ ...prev, [currentIndex]: value }));

  const correctCount = questions.reduce((sum, q, i) => sum + (isCorrectAnswer(q, answers[i]) ? 1 : 0), 0);
  const percent = total > 0 ? Math.round((correctCount / total) * 100) : 0;

  const handleRetake = () => {
    setAnswers({});
    setSubmitted(false);
    setCurrentIndex(0);
  };

  const handleDownload = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      const filename = `${(materialTitle || title || 'quiz').replace(/[^\p{L}\p{N}]+/gu, '_').slice(0, 60)}.pdf`;
      await exportSheetToPdf(printRef.current, filename);
    } catch (err) {
      console.error('Quiz PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  if (!submitted) {
    return (
      <div>
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          {questions.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setCurrentIndex(i)}
              title={`Question ${i + 1}`}
              className={`w-2.5 h-2.5 rounded-full border-none cursor-pointer transition-transform ${
                i === currentIndex ? 'bg-primary scale-125' : answers[i] !== undefined && answers[i] !== '' ? 'bg-primary/40' : 'bg-muted'
              }`}
            />
          ))}
        </div>
        <div className="flex items-center justify-between mb-5">
          <span className="text-xs font-bold text-muted-foreground">Question {currentIndex + 1} of {total}</span>
          <span className="text-xs text-muted-foreground">{answeredCount}/{total} answered</span>
        </div>

        <QuestionPrompt
          question={currentQuestion}
          index={currentIndex}
          answer={answers[currentIndex]}
          onAnswer={setAnswer}
        />

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
            disabled={currentIndex === 0}
            className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-bold text-muted-foreground hover:text-foreground disabled:opacity-30 bg-transparent border-none cursor-pointer disabled:cursor-default"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          {isLast ? (
            <button
              type="button"
              onClick={() => setSubmitted(true)}
              className="px-6 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 border-none cursor-pointer"
            >
              Submit Quiz
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setCurrentIndex((i) => Math.min(total - 1, i + 1))}
              disabled={answers[currentIndex] === undefined || answers[currentIndex] === ''}
              className="flex items-center gap-1 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-40 border-none cursor-pointer disabled:cursor-default"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="text-center py-2 mb-4">
        <ScoreRing percent={percent} />
        <p className="text-2xl font-black text-foreground mt-3">{correctCount}/{total}</p>
        <p className="text-sm text-muted-foreground">correct answers</p>
      </div>

      <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
        <button
          type="button"
          onClick={handleRetake}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-muted hover:bg-muted/70 text-foreground text-xs font-bold border-none cursor-pointer transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Retake Quiz
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={exporting}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-muted hover:bg-muted/70 text-foreground text-xs font-bold border-none cursor-pointer transition-colors disabled:opacity-50"
        >
          <Download className="w-3.5 h-3.5" /> {exporting ? 'Preparing...' : 'Download PDF'}
        </button>
      </div>

      <div className="space-y-3">
        {questions.map((q, i) => {
          const userAnswer = answers[i];
          const correct = isCorrectAnswer(q, userAnswer);
          return (
            <div key={i} className={`p-4 rounded-xl border ${correct ? 'border-accent/40 bg-accent/5' : 'border-destructive/40 bg-destructive/5'}`}>
              <div className="flex items-start gap-2 mb-2">
                {correct ? (
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-accent" />
                ) : (
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-destructive" />
                )}
                <p className="text-sm font-bold text-foreground">{i + 1}. {q.question}</p>
              </div>
              <p className="text-xs text-muted-foreground ml-6">
                Your answer: <span className={`font-semibold ${correct ? 'text-accent' : 'text-destructive'}`}>{userAnswer || '—'}</span>
              </p>
              {!correct && (
                <p className="text-xs text-accent ml-6 mt-0.5">
                  Correct answer: <span className="font-semibold">{q.correctAnswer}</span>
                </p>
              )}
              {q.explanation && (
                <p className="text-xs text-muted-foreground/80 ml-6 mt-1.5 leading-relaxed">{q.explanation}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Off-screen printable sheet used for the PDF export — plain, high-contrast
          styling independent of the site theme so the rasterized PNG stays legible. */}
      <div style={{ position: 'fixed', left: -99999, top: 0, width: 720 }}>
        <div ref={printRef} style={{ padding: 40, background: '#ffffff', color: '#111111', fontFamily: 'Arial, sans-serif' }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>{materialTitle || title || 'Quiz'}</h1>
          <p style={{ fontSize: 12, color: '#555555', marginBottom: 24 }}>{total} questions</p>
          {questions.map((q, i) => (
            <div key={i} style={{ marginBottom: 20, pageBreakInside: 'avoid' }}>
              <p style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>{i + 1}. {q.question}</p>
              {Array.isArray(q.options) && q.options.length > 0 ? (
                <div style={{ marginLeft: 16 }}>
                  {q.options.map((opt, oi) => (
                    <p key={oi} style={{ fontSize: 12, margin: '2px 0', fontWeight: opt === q.correctAnswer ? 700 : 400, color: opt === q.correctAnswer ? '#0f7a3d' : '#333333' }}>
                      {String.fromCharCode(65 + oi)}. {opt}{opt === q.correctAnswer ? '  ✓' : ''}
                    </p>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: 12, marginLeft: 16, fontWeight: 700, color: '#0f7a3d' }}>Answer: {q.correctAnswer}</p>
              )}
              {q.explanation && (
                <p style={{ fontSize: 11, marginLeft: 16, marginTop: 6, color: '#666666', fontStyle: 'italic' }}>{q.explanation}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuizPlayer;
